import { Clipboard } from "@capacitor/clipboard";
import classNames from "classnames";
import ons from "onsenui";
import React from "react";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { isClipboardAvailable } from "../utils/clipboard";
import { withDefault } from "../utils/withDefault";
import { CardButton } from "./CardButton/CardButton";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import { If } from "./Generic/If";
import QuestionHeader from "./QuestionHeader";
import { RequiredDocAnswerableQuestionProps } from "./RequiredDocAnswerableQuestion";
import RequiredDocReadonlyAnswer from "./RequiredDocReadonlyAnswer";
import { scrollToViewAfterKeyboardOpen } from "./ScheduledVisitDocScreenUtils";
import { SpeechRecognitionModal } from "./SpeechRecognitionModal";
import useOpen from "../hooks/useOpen";
import { AppIcon } from "./Generic/AppIcon";
import { OutlineMicIcon } from "../utils/icones";
import { iconColors } from "./Generic/AppIconColors";

type Props = RequiredDocAnswerableQuestionProps;

const RequiredDocShortText = (props: Props) => {
  const { actions, isOpen } = useOpen();

  const handleClickRecord = () => {
    if (props.readonly) {
      return;
    }
    actions.open();
  };

  return (
    <AppCard
      className={classNames({ __invalid: !props.isValid })}
      onClick={() => {
        if (props.readonly && props.onClickWhileDisabled !== undefined) {
          props.onClickWhileDisabled();
        }
      }}
    >
      <QuestionHeader
        text={props.questionText}
        isRequired={props.isRequired && !props.readonly}
        notes={props.notes}
        isReadonly={props.readonly}
        recordAction={
          <AppIcon
            onClick={handleClickRecord}
            fill={iconColors.get("primary")}
            icon={<OutlineMicIcon width={24} />}
          />
        }
      />
      <AppCardContent onClick={(e) => scrollToViewAfterKeyboardOpen(e)}>
        {props.readonly ? (
          <RequiredDocReadonlyAnswer currentAnswer={props.currentAnswer} />
        ) : (
          <input
            placeholder={translate(STRINGS.ENTER_TEXT_HERE)}
            disabled={props.readonly}
            className="ScheduledVisitDocScreen__block text-input text-input--material"
            onChange={(event: any) => {
              props.handleOnInsertAnswer(props.questionId, event.target.value);
            }}
            value={withDefault(props.currentAnswer, withDefault(props.prefilledAnswer, ""))}
          />
        )}
      </AppCardContent>

      <If condition={isClipboardAvailable()}>
        <AppCardContent className="ScheduledVisitDocScreen_longText_CopyPasteIcons">
          <CardButton
            variation="outline"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof props.currentAnswer === "string") {
                Clipboard.write({ string: props.currentAnswer }).then(
                  () => {
                    ons.notification.toast("Copied text to clipboard", {
                      timeout: 1000,
                    });
                  },
                  (err) => {
                    ons.notification.toast(`Error copying text to clipboard ${err}`, {
                      timeout: 1000,
                    });
                  }
                );
              }
            }}
          >
            {translate(STRINGS.COPY)}
          </CardButton>
          <CardButton
            variation="outline"
            onClick={(e) => {
              e.stopPropagation();
              Clipboard.read().then(
                ({ value }) => {
                  props.handleOnInsertAnswer(props.questionId, value);
                },
                (err) => {
                  ons.notification.toast(`Error pasting ${err}`, {
                    timeout: 1000,
                  });
                }
              );
            }}
          >
            {translate(STRINGS.PASTE)}
          </CardButton>
        </AppCardContent>
      </If>
      <If condition={isOpen && !props.readonly}>
        <SpeechRecognitionModal
          isOpen={isOpen}
          onStop={(text) => {
            props.handleOnInsertAnswer(
              props.questionId,
              typeof props.currentAnswer === "string" ? `${props.currentAnswer} ${text}` : text
            );
            actions.dismiss();
          }}
          onCancel={actions.dismiss}
        />
      </If>
    </AppCard>
  );
};

export default RequiredDocShortText;
